// Okubo building-art pass. Scoped by authored world coordinate AND ID because
// east-side IDs are reused by other districts. No map or gameplay changes.
import type { HdBuilding } from '../../common/src/data';
import type { LightFn } from './hd-registry';
import { type G, K, HT, px, frame, disc, alpha, lt } from './hd-kit';
import { panel, fan, window } from './hd-hachi-study';

type Style = 'home' | 'food' | 'gai' | 'neon' | 'market' | 'shrine' | 'service' | 'laundry';
type Unit = readonly [number, number, number, number];
type Spec = readonly [string, number, number, Style, Unit | null];
export const OKUBO_REFRESH: readonly Spec[] = [
  ["oa-n1", 130, 0, "service", null],
  ["oa-n2", 148, 0, "food", null],
  ["oa-n3", 165, 0, "neon", null],
  ["oa-n4", 182, 0, "neon", null],
  ["oa-w1", 130, 12, "food", [320, 38, 96, 44]],
  ["oa-w2", 144, 12, "neon", [160, 30, 96, 44]],
  ["oa-w3", 130, 21, "market", [320, 86, 96, 44]],
  ["oa-w4", 144, 21, "neon", [24, 86, 96, 44]],
  ["oa-e1", 166, 12, "food", [256, 66, 64, 32]],
  ["oa-e2", 166, 20, "gai", null],
  ["oa-e3", 171, 20, "gai", null],
  ["oa-e4", 176, 20, "gai", null],
  ["oa-e5", 183, 12, "market", [352, 22, 96, 44]],
  ["oa-e6", 183, 20, "gai", null],
  ["oa-e7", 188, 20, "gai", null],
  ["oa-e8", 193, 20, "gai", null],
  ["oa-d1", 130, 36, "service", [112, 102, 96, 44]],
  ["oa-d2", 130, 46, "food", [24, 86, 96, 44]],
  ["shrine", 141, 36, "shrine", null],
  ["oa-d4", 153, 36, "gai", [24, 150, 96, 44]],
  ["oa-d5", 153, 46, "gai", [24, 86, 96, 44]],
  ["oa-f1", 166, 36, "neon", null],
  ["oa-f2", 182, 36, "neon", null],
  ["oa-f3", 166, 42, "food", null],
  ["oa-f4", 182, 42, "neon", null],
  ["oa-g1", 166, 48, "market", null],
  ["oa-g2", 176, 48, "gai", null],
  ["oa-g3", 181, 48, "gai", null],
  ["oa-g4", 186, 48, "gai", null],
  ["oa-g5", 192, 48, "gai", null],
  ["ob-w1", 130, 62, "food", [24, 118, 96, 44]],
  ["ob-w2", 130, 72, "neon", [24, 150, 96, 44]],
  ["ob-w3", 145, 62, "neon", [160, 118, 96, 44]],
  ["ob-w4n", 145, 72, "market", null],
  ["ob-w4", 151, 72, "market", [96, 150, 96, 44]],
  ["ob-e1", 166, 62, "food", [24, 86, 96, 44]],
  ["ob-e2", 182, 62, "neon", [24, 106, 40, 24]],
  ["ob-e2b", 188, 62, "service", [128, 86, 96, 44]],
  ["ob-e3", 166, 70, "gai", [24, 182, 96, 44]],
  ["ob-e4", 188, 78, "service", null],
  ["ob-s1", 130, 90, "home", [144, 22, 96, 44]],
  ["ob-s2", 144, 90, "service", [368, 42, 40, 24]],
  ["ob-s3", 166, 90, "service", [256, 22, 96, 44]],
  ["ob-s4", 182, 90, "neon", [352, 22, 96, 44]],
  ["n1", 200, 0, "market", null],
  ["n2", 212, 0, "laundry", null],
  ["n3", 225, 0, "market", null],
  ["n4", 237, 0, "food", null],
  ["e1", 250, 0, "home", [120, 554, 40, 24]],
  ["e2", 250, 20, "home", null],
  ["e3", 250, 40, "home", [24, 506, 64, 32]],
  ["e4", 250, 60, "home", [24, 186, 40, 24]],
  ["e5", 250, 80, "home", null],
  ["e5s", 250, 87, "home", null], // e5's south half: the Sanctuary approach (M10) cuts through y84-86
  ["a1", 206, 12, "home", [24, 330, 40, 24]],
  ["a2", 215, 12, "market", [80, 342, 96, 44]],
  ["b1", 225, 12, "neon", [24, 86, 96, 44]],
  ["b2", 234, 12, "home", [24, 42, 64, 32]],
  ["b2s", 234, 22, "home", [24, 34, 64, 32]],
  ["c1", 206, 36, "food", [256, 18, 64, 32]],
  ["d1", 221, 36, "food", [176, 74, 40, 24]],
  ["d2", 229, 36, "home", null],
  ["f1", 206, 46, "neon", [24, 22, 96, 44]],
  ["f2", 218, 46, "home", null],
  ["f3", 230, 46, "neon", [24, 26, 64, 32]],
  ["g1", 206, 62, "food", [176, 18, 40, 24]],
  ["g2", 230, 62, "service", null],
  ["g3", 232, 62, "market", [24, 106, 40, 24]],
  ["h1", 206, 72, "home", [96, 138, 40, 24]],
  ["h2", 218, 72, "service", [24, 122, 64, 32]],
  ["h3", 226, 72, "market", [24, 170, 40, 24]],
  ["h4", 234, 72, "service", [24, 162, 64, 32]],
  ["i1", 206, 90, "food", [24, 18, 40, 24]],
  ["i2", 218, 90, "home", [296, 18, 40, 24]],
  ["i3", 230, 90, "market", [120, 42, 40, 24]],
];
const SCOPE = new Map(OKUBO_REFRESH.map(s => [`${s[1]}:${s[2]}:${s[0]}`, s]));
function specOf(b: HdBuilding) { return SCOPE.get(`${b.x}:${b.y}:${b.id}`); }

/** Draw below original roof props: retain their silhouettes, laundry and Korean roof jars. */
export function paintOkuboRoof(g: G, b: HdBuilding, x: number, y: number) {
  const s = specOf(b); if (!s) return false;
  const style = s[3], w = b.w * HT, rh = (b.h - b.face) * HT;
  g.save(); g.beginPath(); g.rect(x, y, w, rh); g.clip();
  if (style === 'shrine' || style === 'gai') {
    // Layered ceramic ridges and exposed timber ends, rather than industrial railings.
    const gap = style === 'shrine' ? 46 : 38;
    for (let yy = 22; yy < rh - 14; yy += gap) {
      px(g, x + 11, y + yy + 3, w - 22, 5, alpha(K.night, .35));
      px(g, x + 9, y + yy, w - 18, 3, K.kawaraHi);
      px(g, x + 10, y + yy + 3, w - 20, 2, K.kawaraLo);
      for (let xx = 13; xx < w - 12; xx += 8) px(g, x + xx, y + yy, 1, 4, K.ink);
    }
    px(g, x + 12, y + rh - 12, w - 24, 5, K.woodLo);
    for (let xx = 18; xx < w - 12; xx += 16) px(g, x + xx, y + rh - 12, 4, 8, K.woodHi);
  } else {
    // Coping seams, drainage channel, service track and shallow weathered roof zones.
    frame(g, x + 8, y + 8, w - 16, rh - 16, K.metalLo);
    px(g, x + 9, y + 9, w - 18, 1, K.metalHi);
    for (let xx = 22; xx < w - 12; xx += 32) px(g, x + xx, y + 8, 2, 3, K.ink);
    px(g, x + w - 16, y + 16, 5, rh - 30, K.ink);
    px(g, x + w - 15, y + 16, 2, rh - 30, K.metalLo);
    for (let yy = 28; yy < rh - 16; yy += 32) px(g, x + w - 17, y + yy, 7, 2, K.metal);
    if (rh > 100) {
      for (let yy = 50; yy < rh - 26; yy += 64) {
        px(g, x + 17, y + yy, w - 40, 1, alpha(K.metalHi, .15));
        px(g, x + 18, y + yy + 1, w - 41, 1, alpha(K.night, .24));
      }
      panel(g, x + 16, y + rh - 24, w - 38, 10, K.metalLo);
      for (let xx = 21; xx < w - 25; xx += 8) px(g, x + xx, y + rh - 21, 2, 4, K.ink);
      disc(g, x + 22, y + rh - 34, 5, 3, K.metalHi);
      disc(g, x + 22, y + rh - 34, 2, 1, K.ink);
    }
  }
  const unit = s[4];
  if (unit) {
    const [ux, uy, uw, uh] = unit, xx = x + ux, yy = y + uy;
    px(g, xx + 4, yy + 5, uw, uh, alpha(K.night, .4));
    if (style === 'food' || style === 'market' || style === 'laundry') {
      panel(g, xx, yy, uw, uh, K.metalLo);
      if (uw >= 64 && uh >= 32) {
        fan(g, xx + 4, yy + 3);
        if (uw >= 96) fan(g, xx + 38, yy + 3);
      }
      for (let j = 5; j < uh - 3; j += 5) px(g, xx + uw - 18, yy + j, 13, 1, K.metalHi);
      px(g, xx + 5, yy + uh - 5, uw - 10, 2, K.metalHi);
    } else if (style === 'home' || style === 'gai') {
      // Raised drying/plant deck complements the original clotheslines and onggi.
      panel(g, xx, yy, uw, uh, K.woodLo);
      for (let j = 8; j < uw - 3; j += 8) px(g, xx + j, yy + 3, 1, uh - 6, K.woodHi);
      for (const dx of [6, uw - 21]) {
        panel(g, xx + dx, yy + uh - 15, 15, 10, K.wood);
        px(g, xx + dx + 3, yy + uh - 18, 9, 5, K.leafLo);
        px(g, xx + dx + 5, yy + uh - 20, 3, 7, K.leafHi);
      }
    } else {
      window(g, xx, yy, uw, uh, K.glassLo);
      for (let j = 16; j < uw - 4; j += 16) px(g, xx + j, yy + 3, 2, uh - 6, K.metalHi);
    }
  }
  g.restore();
  return true;
}

function tube(g: G, x: number, y: number, w: number, h: number, c: string) {
  px(g, x - 3, y - 3, w + 6, h + 6, alpha(c, .1));
  px(g, x - 1, y - 1, w + 2, h + 2, alpha(c, .25));
  px(g, x, y, w, h, c);
  if (w > h) px(g, x, y, w, 1, lt(c, .5));
  else px(g, x, y, 1, h, lt(c, .5));
}

/** Keep existing lettering, doors, shop displays, landmarks and their original lights. */
export function paintOkuboFront(g: G, b: HdBuilding, x: number, y: number, L: LightFn) {
  const s = specOf(b); if (!s || !b.face) return;
  const style = s[3], w = b.w * HT, h = b.face * HT, fy = y + (b.h - b.face) * HT, bottom = fy + h;
  g.save(); g.beginPath(); g.rect(x, fy, w, h); g.clip();
  if (style === 'shrine') {
    // Keep the torii silhouette. Warm stone lanterns sit against the hall inside its footprint.
    for (const xx of [x + 56, x + w - 71]) {
      panel(g, xx, bottom - 36, 15, 23, K.concreteLo);
      px(g, xx - 2, bottom - 38, 19, 4, K.concreteHi);
      px(g, xx + 4, bottom - 31, 7, 9, K.warmHi);
      px(g, xx + 7, bottom - 31, 1, 9, K.woodLo);
      L(xx + 2, bottom - 33, 11, 16, K.amber, .32, true);
    }
    g.restore(); return;
  }
  if (style === 'home' || style === 'service') {
    // Quiet homes and working garages frame existing entrances; car-park decks stay readable.
    px(g, x + 3, fy + 3, 2, h - 6, K.metalHi);
    px(g, x + w - 5, fy + 3, 2, h - 6, K.metalLo);
    if (!b.kind.includes('CarPark')) {
      const cw = Math.min(66, w - 14), cx = x + Math.floor((w - cw) / 2);
      px(g, cx, bottom - 44, cw, 4, K.ink);
      px(g, cx + 2, bottom - 43, cw - 4, 1, K.metalHi);
      L(cx + 4, bottom - 40, cw - 8, 3, K.amber, .18, false);
    }
    g.restore(); return;
  }
  const c = style === 'food' || style === 'market' || style === 'gai' ? K.amber : K.pink;
  px(g, x + 3, fy + 3, w - 6, h - 7, alpha(c, style === 'neon' ? .09 : .045));
  // Golden Gai gets timber bay edges and small neon accents, not a giant shared marquee.
  if (style === 'gai') {
    for (const xx of [x + 3, x + w - 8]) {
      px(g, xx, fy + 5, 5, h - 8, K.woodLo);
      px(g, xx + 1, fy + 5, 1, h - 8, K.woodHi);
    }
    tube(g, x + w - 7, fy + 14, 2, Math.min(32, h - 24), K.pink);
    L(x + w - 9, fy + 14, 6, 30, K.pink, .26, false);
  } else {
    // Broken-up tubes leave masonry gaps between the shops and the original sign text intact.
    tube(g, x + 9, fy + 3, w - 18, 2, style === 'food' ? K.pink : c);
    for (const xx of [x + 6, x + w - 8]) tube(g, xx, fy + 20, 2, h - 29, c);
    if (style === 'neon') L(x + 12, fy + 7, w - 24, 18, K.pink, .38, false);
  }
  // Reflection sources stay beside the existing windows, leaving the center doorway clear.
  const pool = Math.min(74, Math.floor(w * .25));
  for (const xx of [x + 12, x + w - 12 - pool]) {
    tube(g, xx, bottom - 6, pool, 2, c);
    L(xx, bottom - 14, pool, 9, c, style === 'neon' ? .46 : .34, true);
  }
  // A substantial cornice edge gives broad modern retail fronts depth.
  if (style === 'market' || style === 'neon' || style === 'laundry') {
    px(g, x + 11, fy + 7, w - 22, 2, K.metalLo);
    for (let xx = 18; xx < w - 12; xx += 38) px(g, x + xx, fy + 7, 3, 4, K.metalHi);
  }
  g.restore();
}
